import { useState, useCallback, useEffect, useRef } from 'react';
import { State as ODataState, CompositeFilterDescriptor } from '@progress/kendo-data-query';
import { Grid, GridColumn, GridDataStateChangeEvent, GridNoRecords, GridCustomFilterCellProps, GridCellProps, GridToolbar } from '@progress/kendo-react-grid';
import { ExcelExport, ExcelExportColumn } from '@progress/kendo-react-excel-export';
import { HeaderThElement } from '@progress/kendo-react-data-tools';
import { Input, InputChangeEvent } from '@progress/kendo-react-inputs';
import { Box, Button, ButtonGroup, Card, CircularProgress } from '@mui/material';
import PageContainerLayout from 'src/pages/PageContainerLayout';
import AdminApi from 'src/api/AdminApi';
import { MaxDiscountLog } from 'src/types/maxDiscountLog';
import { userHasRole } from 'src/utils/userHelpers';
import DateTimeCell from 'src/components/grid/DateTimeCell';

type DatePreset = '30' | '90' | '365';

const PRESET_LABELS: Record<DatePreset, string> = {
  '30': 'Last_30_Days',
  '90': 'Last_90_Days',
  '365': 'Last_Year',
};

const getDateRange = (days: number): { fromDate: Date; toDate: Date } => {
  const toDate = new Date();
  const fromDate = new Date();
  fromDate.setDate(fromDate.getDate() - days);
  return { fromDate, toDate };
};

const buildDateFilter = (days: number): CompositeFilterDescriptor => {
  const { fromDate, toDate } = getDateRange(days);
  return {
    logic: 'and',
    filters: [
      { field: 'createdDate', operator: 'gte', value: fromDate },
      { field: 'createdDate', operator: 'lte', value: toDate },
    ],
  };
};

const dateTimeCell = (cellProps: GridCellProps) => <DateTimeCell {...cellProps} />;

const TextFilterCell = (props: GridCustomFilterCellProps) => {
  const handleChange = (e: InputChangeEvent) => {
    props.onChange({
      value: e.value,
      operator: 'contains',
      syntheticEvent: e.syntheticEvent,
    });
  };

  return (
    <HeaderThElement columnId={props.thProps?.columnId || ''} {...props.thProps}>
      <Input value={props.value || ''} onChange={handleChange} />
    </HeaderThElement>
  );
};

const MaxDiscountRaw = () => {
  const isSalesManager = userHasRole(['Sales Manager']);
  const [activePreset, setActivePreset] = useState<DatePreset>('30');
  const [data, setData] = useState<MaxDiscountLog[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [exporting, setExporting] = useState<boolean>(false);
  const [gridDataState, setGridDataState] = useState<ODataState>({
    take: 50,
    skip: 0,
    sort: [{ field: 'createdDate', dir: 'desc' }],
    filter: buildDateFilter(30),
  });

  const exportRef = useRef<ExcelExport | null>(null);

  const loadData = useCallback((dataState: ODataState) => {
    setLoading(true);
    AdminApi.getMaxDiscountLogs(dataState)
      .then((response) => {
        setData(response.value);
        setTotal(response['@odata.count'] || 0);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        console.error('Failed to fetch Max Discount logs');
      });
  }, []);

  useEffect(() => {
    if (isSalesManager) {
      loadData(gridDataState);
    }
  }, [isSalesManager]);

  const handleDataStateChange = (event: GridDataStateChangeEvent) => {
    setGridDataState(event.dataState);
    loadData(event.dataState);
  };

  const handlePreset = (preset: DatePreset) => {
    setActivePreset(preset);
    const newState: ODataState = {
      ...gridDataState,
      skip: 0,
      filter: buildDateFilter(Number(preset)),
    };
    setGridDataState(newState);
    loadData(newState);
  };

  const handleExport = () => {
    if (!exportRef.current) return;
    // Fetch everything for the current date range — no take/skip, preserve filter + sort
    const exportState: ODataState = {
      sort: gridDataState.sort,
      filter: gridDataState.filter,
    };
    setExporting(true);
    AdminApi.getMaxDiscountLogs(exportState)
      .then((response) => {
        setExporting(false);
        if (exportRef.current) {
          exportRef.current.save(response.value);
        }
      })
      .catch(() => {
        setExporting(false);
        console.error('Failed to export Max Discount logs');
      });
  };

  if (!isSalesManager) {
    return (
      <PageContainerLayout pageHeaderTitle="Max Discount Raw">
        <p>You do not have permission to view this page. Sales Manager access required.</p>
      </PageContainerLayout>
    );
  }

  return (
    <div className="maxdiscountraw-page">
      <PageContainerLayout pageHeaderTitle="Max Discount Raw">
        <Box sx={{ mb: 2 }}>
          <ButtonGroup variant="outlined" size="small">
            <Button
              variant={activePreset === '30' ? 'contained' : 'outlined'}
              onClick={() => handlePreset('30')}
            >
              Last 30 Days
            </Button>
            <Button
              variant={activePreset === '90' ? 'contained' : 'outlined'}
              onClick={() => handlePreset('90')}
            >
              Last 90 Days
            </Button>
            <Button
              variant={activePreset === '365' ? 'contained' : 'outlined'}
              onClick={() => handlePreset('365')}
            >
              Last Year
            </Button>
          </ButtonGroup>
        </Box>
        <ExcelExport
          ref={exportRef}
          fileName={`MaxDiscount_Raw_${PRESET_LABELS[activePreset]}.xlsx`}
        >
          <ExcelExportColumn field="createdDate" title="Created Date" />
          <ExcelExportColumn field="userName" title="User Name" />
          <ExcelExportColumn field="userSalesMgr" title="Sales Manager" />
          <ExcelExportColumn field="customerNumber" title="Cust #" />
          <ExcelExportColumn field="customerName" title="Customer Name" />
          <ExcelExportColumn field="itemNumber" title="Item #" />
          <ExcelExportColumn field="pcat" title="PCAT" />
          <ExcelExportColumn field="pcatName" title="PCAT Name" />
          <ExcelExportColumn field="businessSegment" title="Segment" />
          <ExcelExportColumn field="customerPrice" title="Customer Price" cellOptions={{ format: '$#,##0.00' }} />
          <ExcelExportColumn field="overridePrice" title="Override Price" cellOptions={{ format: '$#,##0.00' }} />
          <ExcelExportColumn field="maxDiscount" title="Max Discount" cellOptions={{ format: '0.00%' }} />
          <ExcelExportColumn field="currentDiscount" title="Current Discount" cellOptions={{ format: '0.00%' }} />
          <ExcelExportColumn field="quantity" title="Qty" />
          <ExcelExportColumn field="sfaOrderId" title="SFA OrderID" />
          <ExcelExportColumn field="categoryManagerName" title="Category Manager" />
        </ExcelExport>
        <Card>
          <Grid
            data={data}
            total={total}
            pageable={{ pageSizes: [50, 200, 500] }}
            sortable
            filterable
            scrollable="scrollable"
            {...gridDataState}
            onDataStateChange={handleDataStateChange}
            style={{ width: '100%', maxHeight: '700px' }}
            dataItemKey="id"
          >
            <GridToolbar>
              <Button
                size="small"
                variant="outlined"
                onClick={handleExport}
                disabled={loading || exporting}
              >
                {exporting ? 'Exporting...' : 'Export to Excel'}
              </Button>
            </GridToolbar>
            <GridColumn field="createdDate" title="Created Date" width={200} filterable={false} cells={{ data: dateTimeCell }} />
            <GridColumn field="userName" title="User Name" width={220} cells={{ filterCell: TextFilterCell }} />
            <GridColumn field="userSalesMgr" title="Sales Manager" width={220} cells={{ filterCell: TextFilterCell }} />
            <GridColumn field="customerNumber" title="Cust #" width={110} cells={{ filterCell: TextFilterCell }} />
            <GridColumn field="customerName" title="Customer Name" width={200} cells={{ filterCell: TextFilterCell }} />
            <GridColumn field="itemNumber" title="Item #" width={120} cells={{ filterCell: TextFilterCell }} />
            <GridColumn field="pcat" title="PCAT" width={100} cells={{ filterCell: TextFilterCell }} />
            <GridColumn field="pcatName" title="PCAT Name" width={180} cells={{ filterCell: TextFilterCell }} />
            <GridColumn field="businessSegment" title="Segment" width={100} cells={{ filterCell: TextFilterCell }} />
            <GridColumn field="customerPrice" title="Customer Price" width={110} filterable={false} format="{0:c2}" />
            <GridColumn field="overridePrice" title="Override Price" width={110} filterable={false} format="{0:c2}" />
            <GridColumn field="maxDiscount" title="Max Discount" width={110} filterable={false} format="{0:p2}" />
            <GridColumn field="currentDiscount" title="Current Discount" width={150} filterable={false} format="{0:p2}" />
            <GridColumn field="quantity" title="Qty" width={70} filterable={false} />
            <GridColumn field="sfaOrderId" title="SFA OrderID" width={110} cells={{ filterCell: TextFilterCell }} />
            <GridColumn field="categoryManagerName" title="Category Manager" width={200} cells={{ filterCell: TextFilterCell }} />
            <GridNoRecords>{loading ? <CircularProgress /> : 'No Max Discount log entries found.'}</GridNoRecords>
          </Grid>
        </Card>
      </PageContainerLayout>
    </div>
  );
};

export default MaxDiscountRaw;
